import { Component, type OnInit, type OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { CommonModule } from '@angular/common';
import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { MatTooltipModule } from '@angular/material/tooltip';
import {MatSidenavModule} from '@angular/material/sidenav';
import {Router} from "@angular/router";
import { Subscription } from 'rxjs';

import * as GLOBALS from './transaction-crypto-global.const.values';
import { HTTPCONSTANTS, MatchMediaService, MerchantService, MerchantChangeService, NotificationService } from '@odin/odin-core';
import type { CryptoTransaction } from '../Models/crypto-transactions';
import { TransactionCryptoDetailsComponent } from './transaction-crypto-details.component';
import { CognitoService, AccessGuardService } from '@odin/odin-authentication';

@Component({
  selector: 'odin-transaction-crypto-page',
  templateUrl: './transaction-crypto-page.component.html',
  styleUrls: ['./transaction-crypto-page.component.css'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ScrollingModule, MatIconModule, MatDividerModule, DatePipe, CommonModule, MatTooltipModule, MatSidenavModule]
})
export class TransactionCryptoPageComponent implements OnInit, OnDestroy {
  rowSize = 62;
  private readonly baseUrl: string = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.odinBaseAddress}`;
  cryptoTransactions: CryptoTransaction[] = [];
  loading: boolean = false;
  currentBuid: string | undefined;
  currentMonekId: string | undefined;
  tabletQuery!: MediaQueryList;
  mobileQuery!: MediaQueryList;
  private merchantChangeSub?: Subscription;

  constructor(
    private merchantService: MerchantService,
    private merchantChangeService: MerchantChangeService,
    private mediaService: MatchMediaService,
    private cognitoService: CognitoService,
    private accessService: AccessGuardService,
    private notificationService: NotificationService,
    private router: Router,
    private readonly http: HttpClient,
    private dialog: MatDialog
  ) {
    this.tabletQuery = this.mediaService.matchMedia(GLOBALS.MAX_SCREEN_WIDTH);
    this.mobileQuery = this.mediaService.matchMedia(GLOBALS.MIN_SCREEN_WIDTH);
  }

  openTransactionDetails(tx: CryptoTransaction) {
    this.dialog.open(TransactionCryptoDetailsComponent, {
      panelClass: 'main-panel',
      data: tx,
      width: '700px',
    });
  }

  ngOnInit() {
    // Lets check if the user has access to crypto transactions
    if (!this.canAccessCryptoTransactions()) {
      this.router.navigate(['/dashboard']);
    }    

    this.currentBuid = this.merchantService.getCurrentBuid()?.id;
    this.currentMonekId = this.merchantService.getCurrentMid()?.monekId;
    if (this.currentBuid && this.currentMonekId) {
      this.getCryptoTransactions({ id: this.currentBuid }, this.currentMonekId);
    }

    // When the merchant changes, refresh the transactions
    this.merchantChangeSub = this.merchantChangeService.merchantChangeEvent.subscribe(() => {
      this.currentBuid = this.merchantService.getCurrentBuid()?.id;
      this.currentMonekId = this.merchantService.getCurrentMid()?.monekId;
      if (this.currentBuid && this.currentMonekId) {
        this.getCryptoTransactions({ id: this.currentBuid }, this.currentMonekId);
      } else {
        this.cryptoTransactions = [];
        this.loading = false;
      }
      if (!this.canAccessCryptoTransactions()) {
        this.router.navigate(['/dashboard']);
      }
    });
  }

  ngOnDestroy() {
    this.merchantChangeSub?.unsubscribe();
  }

  refreshTransactions() {
    if (this.currentBuid && this.currentMonekId) {
      this.getCryptoTransactions({ id: this.currentBuid }, this.currentMonekId);
    } else {
      this.cryptoTransactions = [];
      this.loading = false;
    }
  }

  canAccessCryptoTransactions(): boolean {
    return this.accessService.buidHasFeature('forumpay') && this.accessService.hasNewGrant('int:Crypto')
  }

  getCryptoTransactions(merchantRef: { id: string }, monekId: string) {    
    this.loading = true;
    const url = `${this.baseUrl}/transactions/crypto/${merchantRef.id}?monekId=${monekId}`;
    this.http.get<CryptoTransaction[]>(url).subscribe({
      next: (data) => {
        this.cryptoTransactions = [...data];
        this.loading = false;
      },
      error: (err) => {
        this.cryptoTransactions = [];
        this.loading = false;
        this.notificationService.ErrorSmallDialog(
          `Failed to fetch crypto transactions for this merchant.`,
          err);
      }
    });
  }
}